import { IAnyPlan, IMetric, ITestable, TPlanType } from '@belong/types';

import { getAriaLabel } from '../../../contentBlocks/Metric/Metric.utils';
import { Stack } from '../../../ui';
import Metric from '../../../contentBlocks/Metric';
import { MetricContainer } from '../../../contentBlocks/Metric/Metric.styles';
import { isMobilePlan, isNbnPlan } from '../utils';

interface ICostBreakdown extends ITestable {
  planType: TPlanType;
  plan: IAnyPlan;
  dataBeforePromo?: number;
  priceBeforePromo?: number;
}

/**
 * Use this to consume plan details and embed the Metric and Price Breakdown into the Plan Card
 */
export const CostBreakdown: React.FC<ICostBreakdown & Pick<IMetric, 'alignBottom'>> = ({
  planType,
  plan,
  dataBeforePromo,
  priceBeforePromo,
  alignBottom = false,
  'data-testid': testId
}) => {
  if (isMobilePlan(planType) && plan.data) {
    return (
      <Stack alignX="center" space={{ xs: 'xsmall', md: 'small' }} data-testid={testId}>
        <MetricContainer
          aria-label={
            dataBeforePromo && priceBeforePromo
              ? `Was ${getAriaLabel({
                  value: dataBeforePromo,
                  measurementUnit: plan.data.unit
                })} for ${getAriaLabel({
                  value: priceBeforePromo,
                  priceUnit: plan.price.unit,
                  interval: plan.price.recurringFrequency
                })} now ${getAriaLabel({
                  value: plan.data.value,
                  measurementUnit: plan.data.unit
                })} for ${getAriaLabel({
                  value: plan.price.value,
                  priceUnit: plan.price.unit,
                  interval: plan.price.recurringFrequency
                })}`
              : `${getAriaLabel({
                  value: plan.data.value,
                  measurementUnit: plan.data.unit
                })} for ${getAriaLabel({
                  value: plan.price.value,
                  priceUnit: plan.price.unit,
                  interval: plan.price.recurringFrequency
                })}`
          }
        >
          {/* Metric for data */}
          <Metric
            scalingRoot="M"
            alignBottom={alignBottom}
            previousValue={dataBeforePromo}
            value={plan.data.value}
            measurementUnit={plan.data.unit}
          />
          {/* Metric for price */}
          <Metric
            scalingRoot="M"
            alignBottom={alignBottom}
            previousValue={priceBeforePromo}
            value={plan.price.value}
            priceUnit={plan.price.unit}
            interval={plan.price.recurringFrequency}
          />
        </MetricContainer>
      </Stack>
    );
  }

  if (isNbnPlan(planType) && plan.networkSpeed) {
    return (
      <Stack alignX="center" space={{ xs: 'xsmall', md: 'small' }} data-testid={testId}>
        <Metric
          scalingRoot="XXXL"
          alignBottom={alignBottom}
          previousValue={priceBeforePromo}
          value={plan.price.value}
          priceUnit={plan.price.unit}
          interval={plan.price.recurringFrequency}
          ariaLabel={
            priceBeforePromo
              ? `Was ${getAriaLabel({
                  value: priceBeforePromo,
                  priceUnit: plan.price.unit,
                  interval: plan.price.recurringFrequency
                })} now ${getAriaLabel({
                  value: plan.price.value,
                  priceUnit: plan.price.unit,
                  interval: plan.price.recurringFrequency
                })}`
              : `${getAriaLabel({
                  value: plan.price.value,
                  priceUnit: plan.price.unit,
                  interval: plan.price.recurringFrequency
                })}`
          }
        />
      </Stack>
    );
  }

  return null;
};

CostBreakdown.displayName = 'CostBreakdown';
